import {
  forwardRef,
  FormEvent,
  HTMLProps,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  TYPES,
  U21EncryptedFieldProps,
  U21TextFieldProps,
} from 'app/shared/u21-ui/components/input/text-field/U21TextFieldInputProps';
import styled, { css } from 'styled-components';
import { useTheme } from '@mui/styles';
import useForkRef from '@mui/utils/useForkRef';
import { U21LoadError } from 'app/shared/u21-ui/components/display/U21LoadError';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21Button } from 'app/shared/u21-ui/components/input/U21Button';
import { TokenizationEventData } from 'app/shared/u21-ui/components/input/text-field/models';

export interface EncryptedInputProps
  extends
    Omit<HTMLProps<HTMLInputElement>, 'onBlur' | 'onFocus' | 'type'>,
    Pick<
      U21TextFieldProps,
      'delay' | 'error' | 'disabled' | 'placeholder' | 'required' | 'type'
    > {
  delay?: number;
  encryptedFieldProps: U21EncryptedFieldProps;
  frameError: boolean;
  frameLoading: boolean;
  frameReady: boolean;
  onBlur?: () => void;
  onChange: Exclude<HTMLProps<HTMLInputElement>['onChange'], undefined>;
  onFocus?: () => void;
  onReload: () => void;
  setDecryptionError: Dispatch<SetStateAction<boolean>>;
  setFieldError?: (error?: string) => void;
  setFieldFocus: Dispatch<SetStateAction<boolean>>;
  setFieldReady: Dispatch<SetStateAction<boolean>>;
  setFrameError: Dispatch<SetStateAction<boolean>>;
  setFrameReady: Dispatch<SetStateAction<boolean>>;
  setIsLoading?: (isLoading: boolean) => void;
  value: string;
}

export const EncryptedInput = forwardRef<
  HTMLIFrameElement,
  EncryptedInputProps
>((props: EncryptedInputProps, ref) => {
  const {
    delay = 1200,
    disabled,
    encryptedFieldProps,
    error,
    frameError,
    frameLoading,
    frameReady,
    id,
    maxLength,
    minLength,
    name,
    onBlur,
    onChange,
    onFocus,
    onReload,
    pattern,
    placeholder,
    readOnly,
    required,
    setDecryptionError,
    setFieldError,
    setFieldFocus,
    setFieldReady,
    setFrameError,
    setFrameReady,
    setIsLoading,
    type,
    value,
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const combinedRef = useForkRef(ref, iframeRef);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setFrameError(true);
    }, 10000);
  }, [setFrameError]);

  const theme = useTheme();

  useEffect(() => {
    if (!frameReady) return;

    // Add function to extract styles
    const getInputStyles = () => {
      const styles = {
        'min-height': '40px',
        height: '100%',
        width: '100%',
        padding: '0',
        'text-overflow': 'ellipsis',
        '-webkit-tap-highlight-color': 'transparent',
        'font-size': '16px',
        'touch-action': 'manipulation',
        '-webkit-font-smoothing': 'antialiased',
        'font-variant-numeric': 'tabular-nums',
        'font-feature-settings': '\'tnum\', "tnum"',
        'font-family': theme.typography.fontFamily,
        'font-weight': theme.typography.fontWeightRegular,
        'background-color': theme.palette.background.paper,
        resize: 'none',
        border: 'none',
        outline: 'none',
        ...(type === TYPES.TEXTAREA
          ? {
              'line-height': '23px',
              'white-space': 'pre-wrap',
            }
          : {
              'line-height': '40px',
              'white-space': 'nowrap',
            }),
        ...(disabled
          ? {
              cursor: 'not-allowed',
              color: theme.palette.text.disabled,
            }
          : {
              color: theme.palette.text.primary,
            }),
      };
      return Object.entries(styles)
        .map(([cssKey, cssValue]) => `${cssKey}: ${cssValue}`)
        .join('; ');
    };

    const setupMessage = {
      type: 'SETUP_INPUT_FIELD',
      fieldId: id,
      fieldType: type,
      placeholder,
      required,
      disabled,
      minLength,
      maxLength,
      pattern,
      readOnly,
      token: value,
      authToken: encryptedFieldProps.authTokenFetcher(),
      aad: `type=${encryptedFieldProps.fieldType}&id=${encryptedFieldProps.id}`,
      style: getInputStyles(),
      formStyle:
        'overflow: hidden; margin: 0; padding: 0; height: 100%; width: 100%;',
      selectionStyle: 'background-color: rgba(100, 100, 100, .4);',
      orgId: encryptedFieldProps.orgId,
    };

    iframeRef.current?.contentWindow?.postMessage(
      setupMessage,
      encryptedFieldProps.iframeHost,
    );
  }, [
    disabled,
    encryptedFieldProps,
    error,
    frameReady,
    id,
    maxLength,
    minLength,
    pattern,
    placeholder,
    readOnly,
    required,
    theme,
    type,
    value,
  ]);

  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;
  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;

  useEffect(() => {
    let changeTimeout: NodeJS.Timeout;
    const handleMessage = (event: MessageEvent<TokenizationEventData>) => {
      if (!event.data || event.data.fieldId !== id) {
        return;
      }

      if (event.data.type === 'INPUT_READY') {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        setFieldReady(true);
      }

      if (event.data.type === 'INPUT_BLUR') {
        setFieldFocus(false);
        // Not calling onBlurRef.current?.() because the force of the blur
        // here causes the field to think it's "required" even though there
        // is data in the field.
      }

      if (event.data.type === 'INPUT_FOCUS') {
        setFieldFocus(true);
        onFocusRef.current?.();
      }

      if (event.data.type === 'INPUT_VALIDATION_ERROR') {
        setFieldError?.(event.data.error);
      }

      if (event.data.type === 'INPUT_VALIDATION_SUCCESS') {
        setFieldError?.(undefined);
      }

      if (event.data.type === 'INPUT_DECRYPTION_ERROR') {
        setDecryptionError(true);
        setIsLoading?.(false);
      }

      if (event.data.type === 'INPUT_DECRYPTION_SUCCESS') {
        setDecryptionError(false);
      }

      if (event.data.type === 'TOKENIZED_VALUE') {
        onChange({
          target: {
            name,
            value: event.data.token,
          },
        } as unknown as FormEvent<HTMLInputElement>);
        setIsLoading?.(false);
      }

      if (event.data.type === 'INPUT_CHANGED') {
        // Clear the timeout if it exists to avoid multiple calls
        clearTimeout(changeTimeout);
        setIsLoading?.(true);

        // Wait and force an "auto save"
        changeTimeout = setTimeout(() => {
          iframeRef.current?.contentWindow?.postMessage(
            {
              type: 'GET_TOKENIZED_VALUE',
              fieldId: id,
              authToken: encryptedFieldProps.authTokenFetcher(),
              orgId: encryptedFieldProps.orgId,
              aad: `type=${encryptedFieldProps.fieldType}&id=${encryptedFieldProps.id}`,
            },
            encryptedFieldProps.iframeHost,
          );
        }, delay);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    delay,
    encryptedFieldProps,
    id,
    name,
    onChange,
    setFieldError,
    setDecryptionError,
    setFieldFocus,
    setFieldReady,
    setIsLoading,
  ]);

  if (frameError) {
    if (type === TYPES.TEXTAREA) {
      return <U21LoadError label="encrypted field" onTryAgain={onReload} />;
    }
    return (
      <U21Typography variant="body2">
        Failed to load encrypted field. Please{' '}
        <U21Button color="primary" onClick={onReload} variant="text">
          load again
        </U21Button>
        {!disabled && (
          <>
            {' or '}
            {/* Allows agent to clear the data in the field, helpful when field has non-token data */}
            <U21Button
              color="primary"
              onClick={() => {
                onChange({
                  target: {
                    name,
                    value: '',
                  },
                } as unknown as FormEvent<HTMLInputElement>);
                onReload();
              }}
              variant="text"
            >
              clear data
            </U21Button>
          </>
        )}
        .
      </U21Typography>
    );
  }

  return (
    <EncryptedIframe
      $loading={frameLoading}
      $type={type}
      // apply the CSS of the input element to the iframe
      className="MuiInputBase-input MuiOutlinedInput-input MuiInputBase-inputMultiline MuiInputBase-inputSizeSmall MuiInputBase-inputAdornedEnd"
      ref={combinedRef}
      title={`Encrypted Input ${id}`}
      src={`${encryptedFieldProps.iframeHost}/encrypt-field/${id}?field_type=${type}`}
      onLoad={() => setFrameReady(true)}
    />
  );
});

const EncryptedIframe = styled.iframe<{
  $loading?: boolean;
  $type?: U21TextFieldProps['type'];
}>`
  border: none;
  width: 100%;
  min-height: 40px;
  height: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  flex-grow: 1;
  background: transparent;
  overflow: hidden;

  ${(props) =>
    props.$loading
      ? css`
          visibility: hidden;
        `
      : css``}

  ${(props) =>
    props.$type === TYPES.TEXTAREA
      ? css``
      : css`
          height: 40px;
        `}
`;
